import { PERMISSIONS } from '@ibe/constants';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { NotPermitted, PermissionGuard } from '../auth/permission-guard.js';
import { PropertySelect } from '../components/form/property-select.js';
import { ImageGalleryManager } from '../components/media/image-gallery-manager.js';
import { useApi } from '../lib/api-context.js';
import { useFeedback } from '@ibe/ui';

interface PropertyImage {
  readonly id: string;
  readonly propertyId: string;
  readonly url: string;
  readonly category: string;
  readonly isCover: boolean;
  readonly altText: string;
  readonly caption: string | null;
  readonly displayOrder: number;
  readonly version: number;
}

const categories = [
  { value: 'INTERIOR', label: 'Interior' },
  { value: 'EXTERIOR', label: 'Exterior' },
  { value: 'FACILITY', label: 'Facility' },
] as const;

function apiBase(): string {
  const viteBase = import.meta.env.VITE_API_BASE_URL;
  if (viteBase !== undefined && viteBase !== '') {
    return viteBase;
  }
  return import.meta.env.DEV
    ? `${window.location.protocol}//${window.location.hostname}:3000`
    : window.location.origin;
}

export function PropertyImagesPage(): React.JSX.Element {
  const api = useApi();
  const { showSuccess, showError } = useFeedback();
  const queryClient = useQueryClient();
  const [propertyId, setPropertyId] = useState('');
  const [category, setCategory] = useState<string>('INTERIOR');

  const images = useQuery({
    queryKey: ['property-images', propertyId, category],
    enabled: propertyId !== '',
    queryFn: async () =>
      await api.get<readonly PropertyImage[]>(`/v1/properties/${propertyId}/images`, { category }),
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['property-images', propertyId] });
  };

  const upload = async (file: File): Promise<void> => {
    try {
      const property = await api.get<{ readonly propertyType: string | undefined }>(
        `/v1/properties/${propertyId}`,
        { fields: 'propertyType' },
      );
      const propertyTypeValue = property.propertyType;
      const form = new FormData();
      form.append('file', file);
      form.append('property_id', propertyId);
      form.append('category', category);
      if (propertyTypeValue !== undefined) {
        form.append('property_type', propertyTypeValue);
      }
      const uploaded = await api.postForm<{ readonly id: string }>('/v1/attachments/managed', form);
      await api.post(`/v1/properties/${propertyId}/images`, {
        attachmentId: uploaded.id,
        category,
        altText: file.name.replace(/\.[^.]+$/u, ''),
      });
      await refresh();
      showSuccess('Image uploaded.');
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Image upload failed.');
      throw error;
    }
  };

  return (
    <PermissionGuard permission={PERMISSIONS.PROPERTY_READ} fallback={<NotPermitted />}>
      <section className="space-y-6">
        <header className="rounded-xl border border-border bg-card p-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">
            Property · Media
          </p>
          <h1 className="mt-2 text-2xl font-semibold">Property images</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Upload, reorder and maintain images by property and category.
          </p>
        </header>
        <section className="space-y-4 rounded-xl border border-border bg-card p-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Section 01
            </p>
            <h2 className="text-lg font-semibold">Select gallery context</h2>
          </div>
          <PropertySelect
            value={propertyId}
            onChange={setPropertyId}
            description="Choose the property whose images you want to manage."
          />
        </section>
        {propertyId !== '' ? (
          <ImageGalleryManager<PropertyImage>
            images={images.data ?? []}
            imageBaseUrl={apiBase()}
            categoryOptions={categories}
            category={category}
            onCategoryChange={setCategory}
            onUpload={upload}
            onCover={async (image) => {
              await api.post(`/v1/properties/${propertyId}/images/${image.id}/cover`);
              await refresh();
            }}
            onDelete={async (image) => {
              await api.delete(`/v1/properties/${propertyId}/images/${image.id}`);
              await refresh();
            }}
            onUpdate={async (image, altText) => {
              await api.patch(`/v1/properties/${propertyId}/images/${image.id}`, {
                altText,
                version: image.version,
              });
              await refresh();
            }}
            onReorder={async (ids) => {
              await api.put(`/v1/properties/${propertyId}/images/reorder`, { imageIds: ids });
              await refresh();
            }}
          />
        ) : null}
      </section>
    </PermissionGuard>
  );
}
